export const parseJSON = function parseJSON (val) {
  if (!val) return []
  if (Array.isArray(val)) return val
  if (typeof val === 'string') {
    try { return JSON.parse(val) } catch { return [] }
  }
  return []
}

export const toSlug = function toSlug (name) {
  if (!name) return ''
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
}

export const mapLawyer = function mapLawyer (l) {
  let specializations = l.specializations
  if (typeof specializations === 'string') {
    try { specializations = JSON.parse(specializations) } catch { specializations = [] }
  }
  let languages = l.languages
  if (typeof languages === 'string') {
    try { languages = JSON.parse(languages) } catch { languages = [] }
  }
  const slug = toSlug(l.name_en) + (l.bar_id ? '-' + l.bar_id : '')
  return {
    id: l.id,
    slug,
    nameKa: l.name_ka,
    nameEn: l.name_en,
    barId: l.bar_id,
    status: l.status,
    specializations: specializations || [],
    firmId: l.firm_id,
    firmNameEn: l.firm_name_en || '',
    firmNameKa: l.firm_name_ka || '',
    city: l.city,
    phone: l.phone,
    email: l.email,
    admissionDate: l.admission_date,
    languages: languages || [],
    avatar: l.avatar,
    rating: l.rating || 0,
    reviews: l.reviews || 0,
    bioKa: l.bio_ka,
    bioEn: l.bio_en,
    lat: l.lat,
    lng: l.lng,
    education: parseJSON(l.education),
    experience: parseJSON(l.experience),
    officeAddress: l.office_address,
    website: l.website,
    linkedin: l.linkedin,
    gbaProfileUrl: l.gba_profile_url,
    photoUrl: l.photo_url,
    coverPhoto: l.cover_photo,
    certifications: parseJSON(l.certifications),
    memberships: parseJSON(l.memberships),
    publications: parseJSON(l.publications),
    awards: parseJSON(l.awards),
    isSubscribed: l.is_subscribed || false,
    infoScore: l.info_score || 0
  }
}

export const mapFirm = function mapFirm (f) {
  let practiceAreas = f.practice_areas
  if (typeof practiceAreas === 'string') {
    try { practiceAreas = JSON.parse(practiceAreas) } catch { practiceAreas = [] }
  }
  return {
    id: f.id,
    slug: toSlug(f.name_en),
    nameKa: f.name_ka,
    nameEn: f.name_en,
    address: f.address,
    phone: f.phone,
    email: f.email,
    website: f.website,
    logo: f.logo,
    descriptionKa: f.description_ka,
    descriptionEn: f.description_en,
    practiceAreas: practiceAreas || [],
    city: f.city,
    foundedYear: f.founded_year,
    status: f.status,
    lat: f.lat,
    lng: f.lng,
    lawyerCount: f.lawyer_count || 0
  }
}

export const lawyersFetch = {
  from: 'lawyers',
  order: { by: 'info_score', asc: false },
  limit: 50,
  cache: '5m',
  keepPreviousData: true,
  transform: (data, el) => {
    const lawyers = (data || []).map(mapLawyer)
    const root = el.state.root || el.state
    if (root && root.update) {
      root.update({ lawyers, lawyersLoaded: true }, { preventFetch: true })
    }
    return lawyers
  }
}

export const firmsFetch = {
  from: 'law_firms',
  order: { by: 'name_en', asc: true },
  limit: 50,
  cache: '5m',
  keepPreviousData: true,
  transform: (data, el) => {
    const firms = (data || []).map(mapFirm)
    const root = el.state.root || el.state
    if (root && root.update) {
      root.update({ firms, firmsLoaded: true }, { preventFetch: true })
    }
    return firms
  }
}

export const searchDirectory = async function searchDirectory (query, filters = {}) {
  const SUPABASE_URL = 'https://xhzwvfniejgdvcpobdxm.supabase.co'
  const SUPABASE_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inhoend2Zm5pZWpnZHZjcG9iZHhtIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzM5MTcxMTksImV4cCI6MjA4OTQ5MzExOX0.G2D-hdPv_ulmiEPS2AXZ2vGaykgvAmgTqU1eyKvLtcw'
  const HEADERS = { apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}` }

  const q = query.trim()
  if (!q) return { lawyers: [], firms: [] }

  const nameOr = `or=(name_en.ilike.*${encodeURIComponent(q)}*,name_ka.ilike.*${encodeURIComponent(q)}*)`

  const f = filters
  const filterParts = []
  if (f.cityFilter) filterParts.push(`city=eq.${encodeURIComponent(f.cityFilter)}`)
  if (f.verificationFilter === 'verified') filterParts.push('is_subscribed=eq.true')
  else if (f.verificationFilter === 'unverified') filterParts.push('is_subscribed=neq.true')
  if (f.statusFilter === 'active') filterParts.push('status=eq.active')
  else if (f.statusFilter === 'inactive') filterParts.push('status=neq.active')
  const extra = filterParts.join('&')

  const lawyersUrl = `${SUPABASE_URL}/rest/v1/lawyers?${nameOr}${extra ? '&' + extra : ''}&order=info_score.desc&limit=100`
  const firmsUrl = `${SUPABASE_URL}/rest/v1/law_firms?${nameOr}${extra ? '&' + extra : ''}&order=name_en.asc&limit=50`

  const [lr, fr] = await Promise.all([
    fetch(lawyersUrl, { headers: HEADERS }),
    fetch(firmsUrl, { headers: HEADERS })
  ])
  const [ld, fd] = await Promise.all([lr.json(), fr.json()])

  const seenL = new Set()
  const lawyers = Array.isArray(ld) ? ld.filter(l => {
    const k = l.bar_id || l.id; if (seenL.has(k)) return false; seenL.add(k); return true
  }).map(mapLawyer) : []

  const seenF = new Set()
  const firms = Array.isArray(fd) ? fd.filter(f => {
    if (seenF.has(f.id)) return false; seenF.add(f.id); return true
  }).map(mapFirm) : []

  return { lawyers, firms }
}
